import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Grid, 
    Card, 
    CardContent, 
    Button,
    Divider,
    List,
    ListItem,
    ListItemText,
    Chip,
    CardHeader,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Link
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { Crumbs } from '../Crumbs.jsx';
import ReactMarkdown from 'react-markdown';

const BootGuide = () => {
    const title = "Complete Boot Guide for Two-Step Dancing";
    const desc = "Everything you need to know about choosing the perfect boots for two-step dancing, from styles to shopping tips.";
    
    const [stores, setStores] = useState([]);
    const [storesLoading, setStoresLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedStore, setSelectedStore] = useState(null);

    useEffect(() => {
        const fetchStores = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_HOST}/stores?sort=name&populate=image`);
                const data = await res.json();
                setStores(data.data || []);
            } catch (e) {
                setStores([]);
            }
            setStoresLoading(false);
        };
        fetchStores();
    }, []);

    const handleOpenDialog = (store) => {
        setSelectedStore(store);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedStore(null);
    };

    // Helper to get attribute from store (Strapi v4 compatible)
    const getAttr = (store, field) => store?.[field];

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Helmet>
                    <meta charSet="utf-8"/>
                    <meta property="og:description" content={desc}/>
                    <meta property="twitter:title" content={title}/>
                    <meta property="twitter:description" content={desc}/>
                    <title>{title}</title>
                    <meta property="description" content={desc} />
                    <link rel="canonical" href="https://austin2step.com/boot-guide"/>
                </Helmet>
                <Crumbs crumbs={[
                    {title: "Learn"},
                    {title: "Boot Guide"}
                ]} />
                <Paper>
                    <Box p={2}>
                        <Typography variant="h4" color="primary" gutterBottom>
                            {title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {desc}
                        </Typography>
                    </Box>
                </Paper>
            </Grid>

            {/* Quick Navigation */}
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h5" gutterBottom>
                            Quick Navigation
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={2.4}>
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => {
                                        const element = document.getElementById('boot-styles');
                                        element?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    fullWidth
                                >
                                    Sole Types
                                </Button>
                            </Grid>
                            <Grid item xs={6} sm={2.4}>
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => {
                                        const element = document.getElementById('boot-classes');
                                        element?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    fullWidth
                                >
                                    Heel Types
                                </Button>
                            </Grid>
                            <Grid item xs={6} sm={2.4}>
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => {
                                        const element = document.getElementById('upper-materials');
                                        element?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    fullWidth
                                >
                                    Materials
                                </Button>
                            </Grid>
                            <Grid item xs={6} sm={2.4}>
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => {
                                        const element = document.getElementById('proper-fit');
                                        element?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    fullWidth
                                >
                                    Fit Guide
                                </Button>
                            </Grid>
                            <Grid item xs={6} sm={2.4}>
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => {
                                        const element = document.getElementById('shopping-guide');
                                        element?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    fullWidth
                                >
                                    Shopping Guide
                                </Button>
                            </Grid>
                            {/* <Grid item xs={12} sm={2.4}>
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => {
                                        const element = document.getElementById('shopping-guide');
                                        element?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    fullWidth
                                >
                                    Pricing
                                </Button>
                            </Grid> */}
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            {/* Section 1: Boot Sole Types */}
            <Grid item xs={12}>
                <Card id="boot-styles">
                    <CardContent>
                        <Typography variant="h5" gutterBottom color="primary">
                            Boot Sole Types: The Foundation of Good Dancing
                        </Typography>
                        <Typography variant="body1" paragraph>
                            The sole of your boot is the most critical factor for two-step dancing. The wrong sole can make dancing less enjoyable and possibly cause injury.
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ bgcolor: 'success.dark' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Leather Soles ✓ RECOMMENDED
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            The gold standard for two-step dancing. Essential for proper foot movement and dance technique.
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• Allow smooth sliding and pivoting" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Enable proper two-step footwork" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Break in to perfect slickness over time" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Traditional and authentic for western dancing" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Can be resoled when worn out" />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ bgcolor: 'error.dark' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Rubber Soles ✗ AVOID FOR DANCING
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            Rubber soles are designed for traction and grip, which is the opposite of what you need for dancing.
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• Too much grip prevents smooth movement" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Make turns and spins difficult" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Can cause knee and ankle strain" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Interfere with proper dance technique" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Not suitable for dance floors" />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                        
                        <Box mt={3}>
                            <Card >
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Pro Tip: Breaking In Leather Soles
                                    </Typography>
                                    <Typography variant="body2">
                                        New leather soles can be too slippery at first. Dance on them for a few sessions to achieve the perfect balance of slip and grip. Some dancers lightly scuff new soles with sandpaper or concrete to speed up the break-in process.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Section 2: Heel Types */}
            <Grid item xs={12}>
                <Card id="boot-classes">
                    <CardContent>
                        <Typography variant="h5" gutterBottom color="primary">
                            Heel Types: Traditional vs Roper
                        </Typography>
                        <Typography variant="body1" paragraph>
                            The heel height affects your posture, balance, and dancing comfort. Traditional heels are the most fun but can be challenging for beginners, and can even cause issues.
                        </Typography>
                        
                        <Box mt={3}>
                            <Card  >
                                <CardContent>
                                    <img src="/images/roper-vs-cowboy.png" alt="Roper vs Cowbody Heel Comparison" style={{maxWidth: "100%"}}/>
                                </CardContent>
                            </Card>
                        </Box>

                        <Grid container spacing={3}>
                            
                            
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ bgcolor: 'success.dark' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Roper Heel (1 inch or less)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            Lower, wider heel that prioritizes comfort and stability for extended dancing.
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• More comfortable for long periods" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Better stability and balance" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Easier to walk in" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Great for beginners" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Less formal appearance" />
                                            </ListItem>
                                        </List>
                                        <Box mt={2}>
                                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                <strong>Best for:</strong> Beginners, long dance sessions, comfort priority
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ bgcolor: 'info.dark' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Traditional Heel (1.5-2 inches)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            The classic western boot heel that provides proper posture and authentic western look.
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• Promotes proper dance posture" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Classic western appearance" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• May take time to get used to if you normally wear flat shoes" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Ideal for formal dancing events" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="Note: Taller heels can aggravate lower back pain" />
                                            </ListItem>
                                        </List>
                                        <Box mt={2}>
                                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                <strong>Best for:</strong> Experienced dancers, formal events, authentic western look
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                    </CardContent>
                </Card>
            </Grid>

            {/* Section 3: Upper Materials */}
            <Grid item xs={12}>
                <Card id="upper-materials">
                    <CardContent>
                        <Typography variant="h5" gutterBottom color="primary">
                            Upper Materials: Cow Leather vs Exotic
                        </Typography>
                        <Typography variant="body1" paragraph>
                            The material of your boot upper affects comfort, break-in time, and durability. Here's what you need to know:
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ bgcolor: 'success.dark' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Cow Leather (Recommended for Most)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            The most popular choice for dancing boots due to its softness and easier break-in.
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• Softer and more pliable" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Easier and faster break-in period" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• More comfortable from day one" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Wide variety of colors and finishes" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Generally more affordable" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Ages beautifully with wear" />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ bgcolor: 'warning.dark' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Exotic (Snake, Alligator, Ostrich, Shark)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            Exotic leathers that are tougher and require more patience to break in properly.
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• Tougher and more durable" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Longer, more difficult break-in period" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Unique and eye-catching appearance" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• More expensive than cow leather" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Extremely long-lasting when cared for" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• May be less comfortable or require professional stretching" />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                        
                        <Box mt={3}>
                            <Card >
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Material Recommendations by Experience
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2">
                                                <strong>First-time boot buyers:</strong> Start with cow leather for easier break-in and immediate comfort. For extra comfort, pay attention to the inner lining of the shaft. A fully leather lined inner can make a huge difference in comfort.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2">
                                                <strong>Experienced boot wearers:</strong> Exotic leathers offer unique style and exceptional durability. Really make sure they feel comfortable, some exotics take a bit longer to break in.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Section 4: Fit Guidelines */}
            <Grid item xs={12}>
                <Card id="proper-fit">
                    <CardContent>
                        <Typography variant="h5" gutterBottom color="primary">
                            Proper Fit: Insole vs No Insole Sizing
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Getting the right fit is crucial for comfort and dance performance. Your sizing approach depends on whether you plan to use additional insoles.
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ bgcolor: 'success.dark' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            No Additional Insole
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            Wearing boots as-is with only the factory insole or footbed.
                                        </Typography>
                                        <Box mt={2} mb={2}>
                                            <Typography variant="h4" color="primary" align="center">
                                                Go Half Size Down
                                            </Typography>
                                        </Box>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• Accounts for leather stretching over time" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Ensures snug fit after break-in" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Prevents heel slipping while dancing" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Standard recommendation for western boots" />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ bgcolor: 'info.dark' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            With Sole Liner/Insole
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            Adding cushioned insoles, arch support, or custom orthotics.
                                        </Typography>
                                        <Box mt={2} mb={2}>
                                            <Typography variant="h4" color="primary" align="center">
                                                True Size
                                            </Typography>
                                        </Box>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• Compensates for added thickness of insole" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Maintains proper fit with extra cushioning" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Allows room for foot expansion during dancing" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Prevents cramped feeling with added padding" />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                        
                        <Box mt={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Important Fit Tips
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" paragraph>
                                                <strong>Width matters too:</strong> Don't just focus on length. Make sure you have the right width (B, D, EE, etc.)
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" paragraph>
                                                <strong>Try on in the afternoon:</strong> Your feet swell throughout the day, so afternoon fitting is more accurate.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" paragraph>
                                                <strong>Bring your dance socks:</strong> Wear the same thickness socks you'll dance in when trying on boots.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" paragraph>
                                                <strong>Walk around:</strong> Don't just stand - walk and simulate dance movements in the store.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Section 5: Brand Pricing */}
            {/* <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h5" gutterBottom color="primary">
                            Brand Pricing Guide
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Understanding price ranges by brand helps you budget and choose boots that match your needs and dancing frequency.
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Card variant="outlined" sx={{ bgcolor: 'success.dark', mb: 2 }}>
                                    <CardContent>
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <Typography variant="h6" sx={{ mr: 2 }}>
                                                Budget-Friendly ($50-$150)
                                            </Typography>
                                            <Chip label="Best for Beginners" color="success" size="small" />
                                        </Box>
                                        <Typography variant="body2" paragraph>
                                            Great starter boots that won't break the bank while you learn to dance.
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Brands:</strong> Twisted X ($60-120), Durango ($70-130), Smoky Mountain ($50-100), entry-level Justin ($80-150)
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Card variant="outlined" sx={{ bgcolor: 'info.dark', mb: 2 }}>
                                    <CardContent>
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <Typography variant="h6" sx={{ mr: 2 }}>
                                                Mid-Range ($150-$300)
                                            </Typography>
                                            <Chip label="Most Popular" color="info" size="small" />
                                        </Box>
                                        <Typography variant="body2" paragraph>
                                            The sweet spot for regular dancers - good quality without premium pricing.
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Brands:</strong> Tecovas ($195-275), Justin standard line ($150-250), Tony Lama basics ($160-280), Ariat heritage ($170-300)
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Card variant="outlined" sx={{ bgcolor: 'warning.dark', mb: 2 }}>
                                    <CardContent>
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <Typography variant="h6" sx={{ mr: 2 }}>
                                                Premium ($300-$600)
                                            </Typography>
                                            <Chip label="Serious Dancers" color="warning" size="small" />
                                        </Box>
                                        <Typography variant="body2" paragraph>
                                            High-quality boots for dedicated dancers who want superior comfort and craftsmanship.
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Brands:</strong> Tony Lama premium ($300-500), Anderson Bean ($350-550), Lucchese entry ($400-600), Ariat premium ($300-450)
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Card variant="outlined" sx={{ bgcolor: 'error.dark', mb: 2 }}>
                                    <CardContent>
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <Typography variant="h6" sx={{ mr: 2 }}>
                                                Luxury ($600+)
                                            </Typography>
                                            <Chip label="Investment Pieces" color="error" size="small" />
                                        </Box>
                                        <Typography variant="body2" paragraph>
                                            Handcrafted boots that are works of art, built to last decades with proper care.
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Brands:</strong> Lucchese handmade ($600-2000+), Custom boot makers ($800-3000+), Paul Bond ($1000-2500), Beck ($800-2000)
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                        
                        <Box mt={3}>
                            <Card variant="outlined" sx={{ bgcolor: 'primary.dark' }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Special Spotlight: Tecovas
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        <strong>Price Range:</strong> $295-$500 for most styles
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        <strong>What makes them special:</strong> Tecovas is based right here in Austin. Not only do they make their own boots, they have a wide range of styles to choose from.
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Best for:</strong> Dancers who want quality construction and contemporary styling without premium pricing.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    </CardContent>
                </Card>
            </Grid> */}

            {/* Section 3: Shopping Guide */}
            {/* <Grid item xs={12}>
                <Card id="shopping-guide">
                    <CardContent>
                        <Typography variant="h5" gutterBottom color="primary">
                            Shopping Guide: Where and How to Buy
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Where to Shop
                                        </Typography>
                                        
                                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                                            <strong>Local Western Stores</strong>
                                        </Typography>
                                        <Typography variant="body2" paragraph>
                                            Best for trying on boots and getting proper fitting advice.
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• Boot Barn - Wide selection, knowledgeable staff" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Cavender's - Good brand variety" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Local western wear shops" />
                                            </ListItem>
                                        </List>
                                        
                                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                                            <strong>Online Retailers</strong>
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• Zappos - Excellent return policy" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Amazon - Wide selection, reviews" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Brand websites - Full selection" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Sheplers - Western specialty online" />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Shopping Tips
                                        </Typography>
                                        
                                        <Typography variant="subtitle1" gutterBottom>
                                            <strong>Essential Requirements</strong>
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• Leather soles only (no rubber)" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Proper heel height (1-2 inches)" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Snug but comfortable fit" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Quality construction" />
                                            </ListItem>
                                        </List>
                                        
                                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                                            <strong>Sizing Guidelines</strong>
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• Size down 0.5-1 size from sneakers" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Consider width (B, D, EE)" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Try on in afternoon (feet swell)" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Allow for break-in period" />
                                            </ListItem>
                                        </List>
                                        
                                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                                            <strong>Money-Saving Tips</strong>
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText primary="• Buy used boots (already broken in)" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Shop end-of-season sales" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Check for discontinued models" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText primary="• Consider factory seconds" />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                        
                        <Divider sx={{ my: 3 }} />
                        
                        <Card variant="outlined" sx={{ bgcolor: 'primary.dark' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Quick Decision Guide
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            <strong>New Dancer?</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            Start with entry-level boots ($50-150) like Twisted X or basic Justin models.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            <strong>Regular Dancer?</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            Invest in mid-range boots ($150-400) from Tony Lama, Ariat, or Anderson Bean.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            <strong>Serious Dancer?</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            Consider premium boots ($400+) like Lucchese or custom-made options.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            <strong>On a Budget?</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            Look for used boots or shop sales at Boot Barn and Cavender's.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </CardContent>
                </Card>
            </Grid> */}

            {/* Shopping Section */}
            <Grid item xs={12}>
                <Card id="shopping-guide">
                    <CardContent>
                        <Typography variant="h5" gutterBottom color="primary">
                            Where to Shop for Boots in Austin
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            Explore our curated list of stores in Austin that cater to the dance community. Whether you're looking for western boots, dance shoes, or accessories, these shops have you covered.
                        </Typography>
                        
                        {storesLoading ? (
                            <Box display="flex" justifyContent="center" my={3}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                {stores.map((store, idx) => {
                                    const imageUrl =
                                        store.image?.data?.[0]?.attributes?.url ||
                                        store.image?.[0]?.url ||
                                        store.image?.url ||
                                        null;
                                    return (
                                        <Grid item xs={12} md={6} key={store.id || idx}>
                                            <Card variant="outlined">
                                                <CardHeader title={store.name} />
                                                {imageUrl && (
                                                    <Box
                                                        sx={{
                                                            width: "100%",
                                                            height: 180,
                                                            overflow: "hidden",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            background: "#fafafa"
                                                        }}
                                                    >
                                                        <img
                                                            src={imageUrl}
                                                            alt={store.name}
                                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                        />
                                                    </Box>
                                                )}
                                                <CardContent>
                                                    <Typography variant="body2" gutterBottom>
                                                        {store.location && <span><strong>Location:</strong> {store.location}<br /></span>}
                                                        {store.type && <span><strong>Type:</strong> <Chip size="small" label={store.type} /></span>}
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => handleOpenDialog(store)}
                                                        sx={{ mt: 1 }}
                                                    >
                                                        More Info
                                                    </Button>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                        
                        {stores.length === 0 && !storesLoading && (
                            <Typography color="text.secondary" align="center" sx={{ my: 3 }}>
                                No stores found. Check back soon for updated store listings!
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            {/* Store Info Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {getAttr(selectedStore, "name")}
                </DialogTitle>
                <DialogContent dividers>
                    {selectedStore && (
                        <>
                            {/* Image */}
                            {(() => {
                                const imageUrl =
                                    selectedStore.image?.data?.[0]?.attributes?.url ||
                                    selectedStore.image?.[0]?.url ||
                                    selectedStore.image?.url ||
                                    null;
                                return imageUrl ? (
                                    <Box mb={2} sx={{ width: "100%", textAlign: "center" }}>
                                        <img
                                            src={imageUrl}
                                            alt={selectedStore.name}
                                            style={{ maxWidth: "100%", maxHeight: 200, objectFit: "cover" }}
                                        />
                                    </Box>
                                ) : null;
                            })()}
                            {/* Address fields */}
                            <Typography variant="body2" gutterBottom>
                                {getAttr(selectedStore, "address") && (
                                    <div><strong>Address:</strong> {getAttr(selectedStore, "address")}</div>
                                )}
                                {getAttr(selectedStore, "city") && (
                                    <div><strong>City:</strong> {getAttr(selectedStore, "city")}</div>
                                )}
                                {getAttr(selectedStore, "state") && (
                                    <div><strong>State:</strong> {getAttr(selectedStore, "state")}</div>
                                )}
                                {getAttr(selectedStore, "zip") && (
                                    <div><strong>Zip:</strong> {getAttr(selectedStore, "zip")}</div>
                                )}
                                {getAttr(selectedStore, "location") && (
                                    <div><strong>Location:</strong> {getAttr(selectedStore, "location")}</div>
                                )}
                                {getAttr(selectedStore, "type") && (
                                    <div><strong>Type:</strong> <Chip size="small" label={getAttr(selectedStore, "type")} /></div>
                                )}
                                {getAttr(selectedStore, "instagram") && (
                                    <div>
                                        <strong>Instagram:</strong>{" "}
                                        <a href={`https://instagram.com/${getAttr(selectedStore, "instagram")}`} target="_blank" rel="noopener noreferrer">
                                            @{getAttr(selectedStore, "instagram")}
                                        </a>
                                    </div>
                                )}
                                {getAttr(selectedStore, "website") && (
                                    <div>
                                        <strong>Website:</strong>{" "}
                                        <a href={getAttr(selectedStore, "website")} target="_blank" rel="noopener noreferrer">
                                            {getAttr(selectedStore, "website")}
                                        </a>
                                    </div>
                                )}
                                {getAttr(selectedStore, "fields") && (
                                    <div>
                                        <strong>Fields:</strong> {Array.isArray(getAttr(selectedStore, "fields")) ? getAttr(selectedStore, "fields").join(", ") : getAttr(selectedStore, "fields")}
                                    </div>
                                )}
                            </Typography>
                            {/* Body */}
                            {getAttr(selectedStore, "body") && (
                                <Box mt={2}>
                                    <Typography variant="subtitle1" gutterBottom>About</Typography>
                                    <Typography variant="body2">
                                        <ReactMarkdown components={{ a: Link }}>{getAttr(selectedStore, "body")}</ReactMarkdown>
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default BootGuide;